import type { FieldBindingConfig } from '@wix/wix-data-client-common'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import type { DataBindingComponentModel } from '@wix/data-binding-registry-sdk'
import { isValidFieldBinding } from '../../../business-logic/bindings/validation'

const isBindingCompatible = ({
  binding,
  field,
  bindingDefinition,
  role,
}: {
  binding: FieldBindingConfig
  field: FieldSpec
  bindingDefinition: DataBindingComponentModel
  role: string
}): boolean => {
  const section = bindingDefinition.sections.find(s => s.role === role)
  const propertyDef = section?.bindings.find(b => b.prop === binding.prop)

  return propertyDef ? isValidFieldBinding(field, propertyDef) : false
}

const findBindingByProp = (
  bindings: FieldBindingConfig[],
  prop: string,
): FieldBindingConfig | undefined => bindings.find(b => b.prop === prop)

const canBindingBeRemapped = (
  binding: FieldBindingConfig,
  fieldMap: Record<string, string>,
  newSchema: Schema,
  bindingDefinition: DataBindingComponentModel,
  role: string,
): boolean => {
  const newFieldKey = fieldMap[binding.fieldPath]
  const field = newFieldKey && newSchema.fields[newFieldKey]
  return (
    !!field && isBindingCompatible({ binding, field, bindingDefinition, role })
  )
}

export const remapFieldBindings = (
  fieldBindings: FieldBindingConfig[],
  fieldMap: Record<string, string>,
  respectMainProperty: boolean,
  newSchema: Schema,
  bindingDefinition: DataBindingComponentModel,
  role: string,
) => {
  if (respectMainProperty) {
    // TODO: use `getMainPropForComponent` alongside new component model changes
    const mainProp = bindingDefinition['sections'][0]['bindings'][0].prop
    const mainPropBinding = mainProp
      ? findBindingByProp(fieldBindings, mainProp)
      : null
    const canRemapMainProp =
      mainPropBinding &&
      canBindingBeRemapped(
        mainPropBinding,
        fieldMap,
        newSchema,
        bindingDefinition,
        role,
      )

    if (!canRemapMainProp) {
      return []
    }
  }

  const filteredBindings = fieldBindings.filter(binding =>
    canBindingBeRemapped(binding, fieldMap, newSchema, bindingDefinition, role),
  )

  return filteredBindings.map(binding => {
    const newFieldKey = fieldMap[binding.fieldPath]
    const field = newSchema.fields[newFieldKey]

    return {
      ...binding,
      fieldPath: newFieldKey,
      fieldType: field?.type,
    }
  })
}
